import { Entity } from '../entity';

export interface HomesNSWNoticeContractorResponse {
    _id: string;
    contractorResponseDate: string;
    noticeResponseFromContractor: string;
    planOfAction: string;
    //V2 rename of disputeComments
    rejectionComments: string;
}

//V2 rename of the lahcDisputeResponse sub-document
export interface HomesNSWNoticeHpRejectionResponse {
    _id: string;
    hpAcceptsRejection: string;
    dateOfRejectionAcceptanceDecline: string;
    reasonForDecliningRejection: string;
}

//V2 rename of the contractorDisputeResolution sub-document
export interface HomesNSWNoticeContractorNoticeObjection {
    _id: string;
    objectionToBeSubmitted: string;
    dateOfNoticeObjection: string;
    noticeObjectionComments: string;
    //Field name is per the V2 contract despite the dispute wording
    disputeResolutionFlag: string;
    noticeObjectionCompletedDate: string;
}

//V2 rename of the lahcDisputeResolutionDetails sub-document
export interface HomesNSWNoticeHpNoticeObjectionDetails {
    _id: string;
    //V2 rename of disputeResolutionUpheldRejected
    noticeObjectionOutcome: string;
    noticeObjectionCompletedDate: string;
}

export interface HomesNSWNoticeContractorActions {
    _id: string;
    remediedAsPerNotice: string;
    dateOfRemediation: string;
    remediationComments: string;
}

export interface HomesNSWNoticeFacility {
    _id: string;
    facilityAddress: string;
    //HomesNSW's RAML example names this key 'PRN/FRN', but real traffic sends prnFrn (verified against
    //received notice messages, 26 Aug 2026) — the slashed key never matched, so the property reference
    //was silently dropped. The work order payload has always used prnFrn too.
    prnFrn: string;
}

export interface HomesNSWNoticeEotDetails {
    _id: string;
    eotComments: string;
}

export interface HomesNSWNoticeLineItemAuditFindings {
    _id: string;
    //V2 rename of auditFindingsRecommendations
    auditSummaryRecommendations: string;
    details: string;
}

export interface HomesNSWNoticeLineItemAdditionalComments {
    _id: string;
    //V2 rename of disputeComments
    rejectionComments: string;
    withdrawnComments: string;
    cancelledComments: string;
    shortDescription: string;
    longDescription: string;
}

export interface HomesNSWNoticeDetails extends Entity {
    lineItems: HomesNSWNoticeLineItem[];
    noticeType: string;
    noticeVersionNumber: string;
    noticeVersionId: string;
    noticeStatus: string;
    noticeSubStatus: string;
    workOrderNumber: string;
    noticeIssuedDate: string;
    workProgram: string;
    followUpnoticeReIssuedDate: string;
    noticeId: string;
    contractor: string;
    contractorContactName: string;
    contractType: string;
    contractClause: string;
    contractArea: string;
    isContractNCN: string;
    contractNCNOutcome: string;
    ncnContractArea: string;
    noticeCreatorContactNumber: string;
    noticeCategoryDetail: string;
    noticeResponseDueDate: string;
    noticeRemedyDueDate: string;
    noticeDescription: string;
    //V2 rename of noticeEscalated
    remedyReminder: string;
    totalCostInclGst: string;
    revisedNoticeResponseDueDate: string;
    revisedNoticeRemedyDueDate: string;
    dateOfAcceptReviewResult: string;
    followUpNoticeResponseDueDate: string;
    noticeClosedReason: string;
    noticeClosedDate: string;
    nonConformance: string;
    reassessmentComments: string;
    noticeAuditArea: string;
    noticeAuditSubArea: string;
    direction: string;
    noticeWithdrawnReason: string;
    contractorResponse: HomesNSWNoticeContractorResponse;
    hpRejectionResponse: HomesNSWNoticeHpRejectionResponse;
    contractorNoticeObjection: HomesNSWNoticeContractorNoticeObjection;
    hpNoticeObjectionDetails: HomesNSWNoticeHpNoticeObjectionDetails;
    contractorActions: HomesNSWNoticeContractorActions;
    facility: HomesNSWNoticeFacility;
    eotDetails: HomesNSWNoticeEotDetails;

    //V1 legacy field — superseded (not renamed) by followUpnoticeReIssuedDate; V2 never sends it.
    //Retained so historical values on pre-V2 documents stay readable.
    noticeReIssuedDate: string;
}

export interface HomesNSWNoticeDetailsWithPreviousStatus extends HomesNSWNoticeDetails {
    previousStatus: string;
    //Captured alongside previousStatus so a failed send can roll the sub-status back too — we set it
    //ourselves on the transitions we drive (HomesNSW never echoes those back), so a rollback that
    //restored only the status would leave a sub-status describing a submission that did not happen
    previousSubStatus: string;
}

export interface HomesNSWNoticeLineItem extends Entity {
    noticeLineItemId: string;
    noticeVersionId: string;
    lineItemStatus: string;
    lineItemResponseFromContractor: string;
    /**
     * The response HomesNSW hold for this line, as of their last message. Stamped from every inbound
     * notice and notice update (and once by the V2 conversion), never by a user: users edit
     * lineItemResponseFromContractor, which runs ahead of HomesNSW until a request is sent. A request
     * sends only lines this says are Rejected and the user now has Accepted (WBS-12781).
     */
    homesNSWResponse?: string;
    serviceItemTenderedRateInclGst: string;
    totalCost: string;
    serviceItemCode: string;
    assessmentOutcome: string;
    assessedComments: string;
    serviceItemTenderedRateExclGst: string;
    totalCostExclGst: string;
    creditNoteAmount: string;
    quantity: string;
    comments: string;
    auditFindings: HomesNSWNoticeLineItemAuditFindings;
    additionalComments: HomesNSWNoticeLineItemAdditionalComments;
};

export enum HomesNSWNoticeEntityFormAction {
    SubmitResponse = 'SubmitResponse',
    AcceptAll = 'AcceptAll',
    DisputeAll = 'DisputeAll',
    DisputeResolution = 'DisputeResolution',
    Remedied = 'Remedied',
    //ROM notices only (V2): explicit acceptance of the HP review outcome — non-response does not
    //constitute acceptance
    AcceptReviewResult = 'AcceptReviewResult',
}

export enum HomesNSWNoticeStatus {
    Raised = 'Raised',
    Issued = 'Issued',
    RespondedByContractor = 'Responded by Contractor',
    ResponseReviewComplete = 'Response Review Complete by HP',
    NoticeObjection = 'Notice Objection',
    Remedied = 'Remedied',
    Reassessment = 'Reassessment',
    SubsequentPostWorkInspection = 'Subsequent Post Work Inspection',
    AcceptReviewResult = 'Accept Review Result',
    FollowUpNotice = 'Follow Up Notice',
    Withdrawn = 'Withdrawn',
    Complete = 'Complete',
    Closed = 'Closed',

    //V1 legacy values — display only. Closed notices are never remediated (neither by HomesNSW nor by our
    //cutover migration, per AGS alignment ruling), so these values exist permanently on Closed documents.
    //No guard, transition, or command-availability logic may key on them.
    ReIssued = 'Re-Issued',
    ResponseReviewedByLAHC = 'Response Reviewed by LAHC',
    DisputeResolution = 'Dispute Resolution',
    Reinspection = 'Re-Inspection',
};

//Notice type picklist (Salesforce Reference Data Specification v2.0). A Contract NCN is not a
//separate picklist value — it is an NCN notice carrying the isContractNCN flag.
export enum HomesNSWNoticeType {
    Defect = 'Defect',
    NCN = 'NCN',
    Observation = 'Observation',
    RecoveryOfMoney = 'Recovery of Money',
}

//Action availability by notice type (API spec v2.2, Notice Workflows). Shared by the backend
//command guards and the manage UI so they cannot disagree:
//- Contract NCN: contract-level issue, no work order, no line items — contractors may only add
//  comments and files; respond/accept/reject/objection/remediation are all blocked.
//- Observation: informational only — no contractor actions at all; HomesNSW auto-closes it with
//  reason 'No Response Required'.
export class HomesNSWNoticeTypeRules {

    public static isContractNCN(notice: Partial<HomesNSWNoticeDetails>): boolean {

        const flag = notice?.isContractNCN?.toString().trim().toLowerCase();
        return flag === 'true' || flag === 'yes';
    }

    public static isObservation(notice: Partial<HomesNSWNoticeDetails>): boolean {

        return notice?.noticeType === HomesNSWNoticeType.Observation;
    }

    //Whether the notice has line items at all. This — not the notice type, and not the version — is
    //what decides whether there is anything to respond to (WBS-10889 ruling, 29 Aug 2026):
    //- NCN notices carry no line items, and neither do their follow ups: nothing to accept or reject,
    //  so the only contractor action is Submit Remediation, available as soon as the notice lands.
    //- Defect follow ups DO carry line items regardless of version, so they keep the full response
    //  flow — being a follow up never narrows what the user can do (ruling, 24 Aug 2026).
    //Contract NCN and Observation remain governed by workflowActionsBlockedReason: "no actions at
    //all" is a different rule from "nothing to respond to".
    public static hasLineItems(notice: Partial<HomesNSWNoticeDetails>): boolean {

        return (notice?.lineItems?.length ?? 0) > 0;
    }

    //Non-null when the notice type permits no workflow actions; the string is the user-facing
    //explanation returned by the command guards
    public static workflowActionsBlockedReason(notice: Partial<HomesNSWNoticeDetails>): string | null {

        if (this.isContractNCN(notice))
            return 'Contract NCN notices cannot be responded to — add comments or attach files on the notice instead';

        if (this.isObservation(notice))
            return 'Observation notices are informational only — no response is required and HomesNSW closes them automatically';

        return null;
    }

    //Non-null when the notice has nothing to respond to. Separate from workflowActionsBlockedReason
    //because remediation IS still available on these notices — only the response actions are not.
    public static responseActionsBlockedReason(notice: Partial<HomesNSWNoticeDetails>): string | null {

        if (!this.hasLineItems(notice))
            return 'This notice has no line items to respond to — submit the remediation instead';

        return null;
    }
}

//Returned by Submit Remediation when the notice carries no contractor evidence. V2 mandates
//evidence, but the check cannot tell whether the files already on a notice are relevant to THIS
//remediation, so it warns rather than blocks (WBS-10888 ruling, 29 Aug 2026): the user confirms and
//the command is re-sent with evidenceConfirmed. The submission to HomesNSW is unaffected either way
//— it references its own placeholder file, and real evidence flows as individual file uploads.
export interface HomesNSWNoticeRemediationWarning {
    requiresEvidenceConfirmation: boolean;
}

//V2 notice sub-status picklist (Salesforce Reference Data Specification v2.0 §3.6.14).
//Refines noticeStatus: e.g. 'Responded by Contractor' carries 'Notice Accepted by Contractor' or
//'Notice Rejected by Contractor'; 'Notice Objection' carries In-Progress/Complete.
export enum HomesNSWNoticeSubStatus {
    NoticeAcceptedByContractor = 'Notice Accepted by Contractor',
    NoticeRejectedByContractor = 'Notice Rejected by Contractor',
    NoticeUnchanged = 'Notice Unchanged',
    NoticeUpdated = 'Notice Updated',
    NoticeWithdrawn = 'Notice Withdrawn',
    NoticeObjectionInProgress = 'Notice Objection In-Progress',
    NoticeObjectionComplete = 'Notice Objection Complete',
}

//V2 outcome values HomesNSW writes to hpNoticeObjectionDetails.noticeObjectionOutcome once an
//objection review completes. Upheld auto-closes the notice on the HomesNSW side; Rejected/Updated
//leaves line items to remediate.
export enum HomesNSWNoticeObjectionOutcome {
    ObjectionUpheld = 'Objection Upheld',
    ObjectionRejectedUpdated = 'Objection Rejected/Updated',
}

export enum HomesNSWNoticeLineItemContractorResponse {
    Accepted = 'Accepted',
    Rejected = 'Rejected',
    NoResponse = '',

    //V1 legacy value — display only, exists permanently on Closed documents (see HomesNSWNoticeStatus note)
    Disputed = 'Disputed',
}
